import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import HuttonsWhiteLogo from '../img/Huttons_white.png';

const CORPORATE_COLOR_RGB: [number, number, number] = [26, 35, 126];
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];
const RESULTS_HORIZONTAL_MARGIN = 8;
const RESULTS_FIRST_PAGE_TOP_MARGIN = 40;
const RESULTS_CONTINUATION_TOP_MARGIN = 25;
const RESULTS_BOTTOM_MARGIN = 18;
const COLUMN_WIDTH_SAMPLE_LIMIT = 200;
const BODY_WIDTH_PERCENTILE = 0.85;
const CELL_HORIZONTAL_PADDING = 4;
const MIN_TEXT_COLUMN_WIDTH = 12;
const MIN_NUMERIC_COLUMN_WIDTH = 10;
const MAX_MEASURED_COLUMN_WIDTH = 55;

interface UserInfo {
  Name?: string;
  Mobile?: string;
  Email?: string;
  CEANumber?: string;
  Photo?: string;
}

interface ProjectResearchPdfParams {
  userInfo: UserInfo | null;
  viewLabel: string;
  subject: string;
  coverSubject?: string;
  columns: string[];
  rows: string[][];
  filterSummary?: string[];
  resultSummary?: string[];
  filename?: string;
  reportTitle?: string;
}

const isNumericColumn = (column: string): boolean => {
  const normalized = column.toLowerCase();
  return (
    normalized.includes('price') ||
    normalized.includes('psf') ||
    normalized.includes('outlay') ||
    normalized.includes('installment') ||
    normalized.includes('area') ||
    normalized.includes('avail') ||
    normalized === 'matched' ||
    normalized === 'top year' ||
    normalized === 'walking time' ||
    normalized === 'total units' ||
    normalized === 'sold units'
  );
};

const shouldKeepOnOneLine = (column: string): boolean => {
  const normalized = column.trim().toLowerCase();
  return (
    normalized.startsWith('price') ||
    normalized.startsWith('outlay') ||
    normalized.startsWith('installment')
  );
};

const getPercentile = (values: number[], percentile: number): number => {
  if (values.length === 0) return 0;
  const sortedValues = [...values].sort((a, b) => a - b);
  const index = Math.max(0, Math.ceil(percentile * sortedValues.length) - 1);
  return sortedValues[index];
};

const getRepresentativeRows = (rows: string[][]): string[][] => {
  if (rows.length <= COLUMN_WIDTH_SAMPLE_LIMIT) return rows;

  return Array.from({ length: COLUMN_WIDTH_SAMPLE_LIMIT }, (_, index) => {
    const rowIndex = Math.round((index * (rows.length - 1)) / (COLUMN_WIDTH_SAMPLE_LIMIT - 1));
    return rows[rowIndex];
  });
};

const getTextWidth = (doc: jsPDF, value: string | undefined): number => {
  const text = String(value ?? '').trim();
  if (!text) return 0;

  return text
    .split(/\r?\n/)
    .reduce((widestLine, line) => Math.max(widestLine, doc.getTextWidth(line.trim())), 0);
};

const fitWidthsToPage = (
  preferredWidths: number[],
  minimumWidths: number[],
  availableWidth: number
): number[] => {
  if (preferredWidths.length === 0) return [];

  const totalMinimumWidth = minimumWidths.reduce((sum, width) => sum + width, 0);
  const totalPreferredWidth = preferredWidths.reduce((sum, width) => sum + width, 0);
  let fittedWidths: number[];

  if (totalMinimumWidth >= availableWidth) {
    const scale = availableWidth / totalMinimumWidth;
    fittedWidths = minimumWidths.map((width) => width * scale);
  } else if (totalPreferredWidth > availableWidth) {
    const totalFlexibleWidth = preferredWidths.reduce(
      (sum, width, index) => sum + Math.max(0, width - minimumWidths[index]),
      0
    );
    const flexibleScale = totalFlexibleWidth > 0
      ? (availableWidth - totalMinimumWidth) / totalFlexibleWidth
      : 0;
    fittedWidths = preferredWidths.map(
      (width, index) => minimumWidths[index] + Math.max(0, width - minimumWidths[index]) * flexibleScale
    );
  } else {
    const extraWidth = availableWidth - totalPreferredWidth;
    fittedWidths = preferredWidths.map(
      (width) => width + extraWidth * (width / totalPreferredWidth)
    );
  }

  // Keep the total exact so jsPDF-AutoTable cannot add an overflow sliver from rounding.
  const allocatedWidth = fittedWidths.slice(0, -1).reduce((sum, width) => sum + width, 0);
  fittedWidths[fittedWidths.length - 1] = availableWidth - allocatedWidth;
  return fittedWidths;
};

const buildColumnStyles = (
  doc: jsPDF,
  columns: string[],
  rows: string[][],
  pageWidth: number,
  fontSize: number
) => {
  const availableWidth = Math.max(1, pageWidth - RESULTS_HORIZONTAL_MARGIN * 2);
  const representativeRows = getRepresentativeRows(rows);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(fontSize);
  const headerWidths = columns.map((column) => getTextWidth(doc, column));

  doc.setFont('helvetica', 'normal');
  const maximumBodyWidths: number[] = [];
  const measuredWidths = columns.map((_, columnIndex) => {
    const bodyWidths = representativeRows
      .map((row) => getTextWidth(doc, row[columnIndex]))
      .filter((width) => width > 0);
    maximumBodyWidths[columnIndex] = bodyWidths.length > 0 ? Math.max(...bodyWidths) : 0;
    const representativeBodyWidth = getPercentile(bodyWidths, BODY_WIDTH_PERCENTILE);
    return Math.max(headerWidths[columnIndex], representativeBodyWidth) + CELL_HORIZONTAL_PADDING;
  });

  // The body percentile handles unusually long cells within a column. This second
  // cap prevents one unusually verbose column from consuming most of the table.
  const typicalMeasuredWidth = getPercentile(measuredWidths, 0.75);
  const robustMaximumWidth = Math.min(
    MAX_MEASURED_COLUMN_WIDTH,
    Math.max(28, typicalMeasuredWidth * 1.75)
  );
  const minimumWidths = columns.map((column, index) => {
    const baseMinimumWidth = isNumericColumn(column)
      ? MIN_NUMERIC_COLUMN_WIDTH
      : MIN_TEXT_COLUMN_WIDTH;

    // These are the key monetary figures in Unit View. Reserve enough space
    // for both their heading and widest representative value so AutoTable
    // does not split a currency amount over two lines.
    if (shouldKeepOnOneLine(column)) {
      return Math.max(
        baseMinimumWidth,
        headerWidths[index] + CELL_HORIZONTAL_PADDING,
        maximumBodyWidths[index] + CELL_HORIZONTAL_PADDING
      );
    }

    return baseMinimumWidth;
  });
  const preferredWidths = measuredWidths.map((width, index) =>
    Math.max(minimumWidths[index], Math.min(width, robustMaximumWidth))
  );
  const fittedWidths = fitWidthsToPage(preferredWidths, minimumWidths, availableWidth);

  return columns.reduce<Record<number, {
    cellWidth?: number;
    halign?: 'left' | 'center' | 'right';
    overflow?: 'linebreak' | 'ellipsize' | 'visible' | 'hidden';
  }>>((acc, column, index) => {
    acc[index] = {
      cellWidth: fittedWidths[index],
      halign: isNumericColumn(column) ? 'right' : 'left',
      ...(shouldKeepOnOneLine(column) ? { overflow: 'hidden' as const } : {}),
    };
    return acc;
  }, {});
};

const hasSubject = (subject: string): boolean => subject.trim().length > 0;

const renderResultsIntro = (
  doc: jsPDF,
  subject: string,
  filterText: string,
  resultSummary: string[]
): number => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - RESULTS_HORIZONTAL_MARGIN * 2;
  let cursorY = 30;

  if (hasSubject(subject)) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...CORPORATE_COLOR_RGB);
    const subjectLines = doc.splitTextToSize(subject, contentWidth) as string[];
    doc.text(subjectLines, RESULTS_HORIZONTAL_MARGIN, cursorY);
    cursorY += Math.max(1, subjectLines.length) * 4 + 1;
  }

  const summaryItems = resultSummary.map((item) => item.trim()).filter(Boolean);
  const boxTextX = RESULTS_HORIZONTAL_MARGIN + 4;
  const boxTextWidth = contentWidth - 8;
  const columnGap = 8;
  const hasSummary = summaryItems.length > 0;
  const summaryColumnWidth = hasSummary ? boxTextWidth * 0.34 : 0;
  const filterColumnWidth = hasSummary
    ? boxTextWidth - summaryColumnWidth - columnGap
    : boxTextWidth;
  const filterTextX = hasSummary
    ? boxTextX + summaryColumnWidth + columnGap
    : boxTextX;
  const summaryLineHeight = 3.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const summaryLines = hasSummary
    ? doc.splitTextToSize(summaryItems.join(' | '), summaryColumnWidth) as string[]
    : [];
  // Wrap each filter segment independently so long project names/values cannot
  // escape the filter column (jsPDF may otherwise leave very long tokens intact).
  const filterLines = filterText
    .split(/\s*\|\s*/)
    .flatMap((segment) => doc.splitTextToSize(segment, filterColumnWidth) as string[])
    .filter(Boolean);
  const contentStartY = 9;
  const maxFilterLines = 10;
  if (filterLines.length > maxFilterLines) {
    filterLines.splice(maxFilterLines - 1, filterLines.length - maxFilterLines + 1, `${filterLines[maxFilterLines - 1]} …`);
  }
  const contentLineCount = Math.max(summaryLines.length, filterLines.length, 1);
  const summaryBoxHeight = contentStartY + contentLineCount * summaryLineHeight + 2;

  doc.setFillColor(239, 246, 255);
  doc.setDrawColor(...CORPORATE_COLOR_RGB);
  doc.setLineWidth(0.25);
  doc.roundedRect(
    RESULTS_HORIZONTAL_MARGIN,
    cursorY,
    contentWidth,
    summaryBoxHeight,
    1.5,
    1.5,
    'FD'
  );
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...CORPORATE_COLOR_RGB);
  if (hasSummary) doc.text('RESULT SUMMARY', boxTextX, cursorY + 4.5);

  if (hasSummary) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(15, 23, 42);
    doc.text(summaryLines, boxTextX, cursorY + contentStartY);

    const dividerX = boxTextX + summaryColumnWidth + columnGap / 2;
    doc.setDrawColor(190, 205, 225);
    doc.setLineWidth(0.2);
    doc.line(dividerX, cursorY + 3, dividerX, cursorY + summaryBoxHeight - 3);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...CORPORATE_COLOR_RGB);
  doc.text('FILTERS APPLIED', filterTextX, cursorY + 4.5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(90, 100, 115);
  doc.text(filterLines, filterTextX, cursorY + contentStartY);
  cursorY += summaryBoxHeight + 2.5;

  return Math.max(RESULTS_FIRST_PAGE_TOP_MARGIN, Math.ceil(cursorY));
};

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const timeout = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => {
      clearTimeout(timeout);
      resolve(img);
    };
    img.onerror = () => {
      clearTimeout(timeout);
      reject(new Error('failed'));
    };
    img.src = url;
  });

const loadUserPhoto = async (
  doc: jsPDF,
  userInfo: UserInfo,
  photoX: number,
  photoY: number,
  photoRadius: number
): Promise<boolean> => {
  if (!userInfo.Photo) return false;
  try {
    const photoPath = userInfo.Photo.startsWith('/') ? userInfo.Photo.substring(1) : userInfo.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const size = photoRadius * 2;
            const px = Math.max(256, Math.round(size * 12));
            canvas.width = px;
            canvas.height = px;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            const r = px / 2;
            const maxInCircle = r * Math.SQRT2;
            const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
            const sw = Math.round(img.width * scale);
            const sh = Math.round(img.height * scale);
            const sx = Math.round((px - sw) / 2);
            const sy = Math.round((px - sh) / 2);
            ctx.save();
            ctx.beginPath();
            ctx.arc(r, r, r, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, sx, sy, sw, sh);
            ctx.restore();
            doc.addImage(canvas.toDataURL('image/png'), 'PNG', photoX - photoRadius, photoY - photoRadius, size, size);
          }
          doc.setDrawColor(...ORANGE_RGB);
          doc.setLineWidth(2);
          doc.circle(photoX, photoY, photoRadius, 'D');
          resolve(true);
        } catch {
          resolve(false);
        }
      };
      img.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };
      img.src = photoUrl;
    });
  } catch {
    return false;
  }
};

const addHeader = (doc: jsPDF, title: string, pageNum: number, totalPages: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');
  doc.setFillColor(...CORPORATE_COLOR_RGB);
  doc.rect(0, 8, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 10, 18);
  doc.text(`PAGE ${pageNum}/${totalPages}`, pageWidth - 10, 18, { align: 'right' });
  doc.setTextColor(0, 0, 0);
};

const addFooter = (doc: jsPDF, pageNum: number, totalPages: number, userInfo: UserInfo | null, logoDataUrl?: string, logoAspect?: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 15;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  if (logoDataUrl && logoAspect) {
    const logoH = 7;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } else {
    doc.text('Huttons', 10, footerY + 8);
  }
  doc.text(`${pageNum}/${totalPages}`, pageWidth / 2, footerY + 8, { align: 'center' });
  if (userInfo) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont('helvetica', 'bold');
    if (userInfo.Name) doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    if (userInfo.CEANumber) doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
  }
};

const renderCoverPage = async (
  doc: jsPDF,
  userInfo: UserInfo | null,
  title: string,
  subject: string,
  logoDataUrl?: string,
  logoAspect?: number
) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFillColor(...DARK_BG_RGB);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');

  let yPos = 25;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    const aspect = logoImg.width / logoImg.height;
    const logoHeight = 10;
    const logoWidth = logoHeight * aspect;
    const logoX = (pageWidth - logoWidth) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = logoImg.width;
    canvas.height = logoImg.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(logoImg, 0, 0);
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', logoX, yPos - logoHeight + 1, logoWidth, logoHeight);
    } else {
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
    }
  } catch {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  }

  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('SINGAPORE\'S LARGEST PRIVATE REAL ESTATE AGENCY', pageWidth / 2, yPos, { align: 'center' });

  yPos += 8;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, 'F');

  yPos += 20;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth / 2, yPos, { align: 'center' });

  if (hasSubject(subject)) {
    yPos += 15;
    doc.setFontSize(20);
    doc.setTextColor(...ORANGE_RGB);
    doc.text(subject, pageWidth / 2, yPos, { align: 'center', maxWidth: pageWidth - 40 });
    yPos += 30;
  } else {
    yPos += 18;
  }
  const infoBoxWidth = 120;
  const infoBoxHeight = 50;
  const infoBoxX = (pageWidth - infoBoxWidth) / 2;
  const infoBoxY = yPos;
  doc.setFillColor(...INFO_BOX_BG_RGB);
  doc.setDrawColor(...ORANGE_RGB);
  doc.setLineWidth(2);
  doc.roundedRect(infoBoxX, infoBoxY, infoBoxWidth, infoBoxHeight, 3, 3, 'FD');

  if (userInfo) {
    const photoRadius = 18;
    const photoPadding = 10;
    const photoX = infoBoxX + photoPadding + photoRadius;
    const photoY = infoBoxY + infoBoxHeight / 2;

    const photoLoaded = await loadUserPhoto(doc, userInfo, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const initials = userInfo.Name ? userInfo.Name.split(' ').map((name) => name[0]).join('').substring(0, 2).toUpperCase() : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }

    const textStartX = infoBoxX + photoPadding + photoRadius * 2 + 15;
    let detailY = infoBoxY + 12;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    if (userInfo.Name) {
      doc.text(userInfo.Name, textStartX, detailY);
      detailY += 7;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (userInfo.CEANumber) {
      const ceaLabel = 'CEA Registration: ';
      doc.setTextColor(255, 255, 255);
      doc.text(ceaLabel, textStartX, detailY);
      doc.setTextColor(...ORANGE_RGB);
      doc.setFont('helvetica', 'bold');
      doc.text(userInfo.CEANumber, textStartX + doc.getTextWidth(ceaLabel), detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      detailY += 6;
    }
    if (userInfo.Email) {
      doc.text(userInfo.Email, textStartX, detailY);
      detailY += 6;
    }
    if (userInfo.Mobile) {
      doc.text(userInfo.Mobile, textStartX, detailY);
    }
  }

  yPos = infoBoxY + infoBoxHeight + 20;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, yPos, { align: 'center' });

  yPos += 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, 'F');

  yPos += 12;
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hours = now.getHours();
  const mins = String(now.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  const disclaimer = `Information and materials as of ${dd}/${mm}/${yyyy}, ${hours12}:${mins} ${ampm}. Subject to change without prior notice.`;
  const lines = doc.splitTextToSize(disclaimer, pageWidth - 40);
  doc.text(lines, pageWidth / 2, yPos, { align: 'center' });

  addFooter(doc, 1, 1, userInfo, logoDataUrl, logoAspect);
};

export const exportProjectResearchPdf = async ({
  userInfo,
  viewLabel,
  subject,
  coverSubject,
  columns,
  rows,
  filterSummary = [],
  resultSummary = [],
  filename,
  reportTitle = 'PROJECT RESEARCH REPORT',
}: ProjectResearchPdfParams): Promise<void> => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  let logoDataUrl: string | undefined;
  let logoAspect: number | undefined;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    const canvas = document.createElement('canvas');
    canvas.width = logoImg.width;
    canvas.height = logoImg.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(logoImg, 0, 0);
      logoDataUrl = canvas.toDataURL('image/png');
      logoAspect = logoImg.width / logoImg.height;
    }
  } catch {
    // Fall back to text branding in header/footer.
  }

  await renderCoverPage(doc, userInfo, reportTitle, coverSubject ?? subject, logoDataUrl, logoAspect);

  doc.addPage('a4', 'landscape');

  const title = `${viewLabel.toUpperCase()} RESULTS`;
  const appliedFilters = filterSummary.map((filter) => filter.trim()).filter(Boolean);
  const filterText = appliedFilters.length > 0
    ? appliedFilters.join(' | ')
    : 'No table filters applied';
  const resultsPageWidth = doc.internal.pageSize.getWidth();
  const tableStartY = renderResultsIntro(doc, subject, filterText, resultSummary);
  const compactFontSize = columns.length > 20 ? 5.5 : columns.length > 15 ? 6 : 7;

  autoTable(doc, {
    startY: tableStartY,
    head: [columns],
    body: rows.length > 0 ? rows : [['No data available']],
    theme: 'grid',
    margin: {
      // `startY` controls the first results page. This compact margin is used
      // for continuation pages and begins just below the 23 mm page header.
      top: RESULTS_CONTINUATION_TOP_MARGIN,
      right: RESULTS_HORIZONTAL_MARGIN,
      bottom: RESULTS_BOTTOM_MARGIN,
      left: RESULTS_HORIZONTAL_MARGIN,
    },
    tableWidth: resultsPageWidth - RESULTS_HORIZONTAL_MARGIN * 2,
    styles: {
      fontSize: compactFontSize,
      cellPadding: 2,
      overflow: 'linebreak',
      lineColor: [220, 224, 230],
      lineWidth: 0.1,
      textColor: [15, 23, 42],
      valign: 'middle',
    },
    headStyles: {
      fillColor: CORPORATE_COLOR_RGB,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: compactFontSize,
      minCellHeight: 10,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: buildColumnStyles(doc, columns, rows, resultsPageWidth, compactFontSize),
    rowPageBreak: 'avoid',
    didParseCell: (hookData) => {
      if (rows.length === 0 && hookData.section === 'body') {
        hookData.cell.styles.halign = 'center';
      }
    },
  });

  const totalPages = doc.getNumberOfPages();
  doc.setPage(1);
  addFooter(doc, 1, totalPages, userInfo, logoDataUrl, logoAspect);
  for (let page = 2; page <= totalPages; page += 1) {
    doc.setPage(page);
    addHeader(doc, title, page, totalPages);
    addFooter(doc, page, totalPages, userInfo, logoDataUrl, logoAspect);
  }

  const safeFilename = filename ?? `project-research-${viewLabel.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  doc.save(safeFilename);
};
